import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    ArrowLeft,
    // Edit3, (the Edit button is hidden for now)
    Trash2,
    CheckCircle2,
    User,
    Building2,
    Calendar,
    Plus
} from 'lucide-react';
import { initialDummyUsers } from '../data/mockUsers';
import { deleteUser, fetchEveryUser, normalizeUserData } from '../services/adminService';
import AssignTeamMemberModal from '../components/AssignTeamMemberModal';
import TeamStructure from '../components/TeamStructure/TeamStructure';
import UserProfileSections from '../components/UserProfileSections';
import './UserDetails.css';

const UserDetails = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [activeTab, setActiveTab] = useState('profile');
    const [isDeleting, setIsDeleting] = useState(false);
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState(null);
    const [treeVersion, setTreeVersion] = useState(0); // changes when the team structure must be loaded again

    // Initial user passed via router state or find by ID in initialDummyUsers
    const userFromState = location.state?.user;
    const initialUser = userFromState || initialDummyUsers.find(u => String(u.id) === String(userId) || String(u._id) === String(userId)) || initialDummyUsers[0];
    const [currentUser, setCurrentUser] = useState(initialUser);

    useEffect(() => {
        if (userFromState) {
            setCurrentUser(userFromState);
        } else if (userId) {
            fetchEveryUser().then(list => {
                const found = list.find(u => String(u._id) === String(userId) || String(u.id) === String(userId));
                if (found) {
                    setCurrentUser(normalizeUserData(found));
                }
            }).catch(err => {
                console.warn('Failed to load user info from server:', err);
            });
        }
    }, [userId, userFromState]);

    const isTeamLead = (currentUser?.role || '').toLowerCase().includes('lead') || (currentUser?.rawRole || '').toLowerCase().includes('lead');
    const treeUserId = String(currentUser?._id || currentUser?.id || userId || '');

    const handleAssignSuccess = (assignedUser) => {
        if (!assignedUser) return;

        setToastMessage(`Successfully assigned ${assignedUser.name} to team lead.`);
        setTimeout(() => setToastMessage(null), 3500);

        // Load the team structure again so the new member shows up
        setTreeVersion((version) => version + 1);
    };

    const handleDelete = async () => {
        const id = currentUser?._id || currentUser?.id || userId;
        const confirmed = window.confirm(`Are you sure you want to permanently delete user "${currentUser.name || 'this user'}"?`);
        if (!confirmed) return;

        setIsDeleting(true);
        try {
            await deleteUser(id);
            navigate('/users-roles');
        } catch (err) {
            console.error('Failed to delete user:', err);
            const errMsg = err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to remove user.';
            alert(errMsg);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className="user-details-page">
            {/* Top Back & Header Bar */}
            <div className="user-details-header">
                <div className="header-left-group">
                    <button
                        className="back-nav-btn"
                        onClick={() => navigate('/users-roles')}
                        title="Back to Users & Roles"
                        type="button"
                    >
                        <ArrowLeft size={18} />
                    </button>

                    <div
                        className="user-main-avatar"
                        style={{ backgroundColor: currentUser.avatarColor || '#8B5CF6' }}
                    >
                        {currentUser.initials || 'U'}
                    </div>

                    <div className="user-header-text">
                        <div className="name-and-status">
                            <h2 className="user-full-name">{currentUser.name}</h2>
                            <span className="user-status-badge">
                                <span className="status-dot-active"></span>
                                {currentUser.status_label || 'Approved'}
                            </span>
                        </div>
                        <span className="user-header-designation">
                            {currentUser.designation || currentUser.role}
                        </span>
                    </div>
                </div>

                <div className="header-actions-group">
                    {/* Edit is hidden for now (it has no action yet):
                    <button className="user-btn-edit" type="button">
                        <Edit3 size={15} />
                        <span>Edit</span>
                    </button>
                    */}
                    <button
                        className="user-btn-delete"
                        onClick={handleDelete}
                        disabled={isDeleting}
                        type="button"
                    >
                        <Trash2 size={15} />
                        <span>{isDeleting ? 'Deleting...' : 'Delete'}</span>
                    </button>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="user-details-tabs">
                <button
                    className={`user-tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
                    onClick={() => setActiveTab('profile')}
                    type="button"
                >
                    Profile
                </button>
                <button
                    className={`user-tab-btn ${activeTab === 'team' ? 'active' : ''}`}
                    onClick={() => setActiveTab('team')}
                    type="button"
                >
                    Team Structure
                </button>
            </div>

            {/* Main Content: 2-Column Layout */}
            <div className="user-details-body">
                {/* Left Panel */}
                <div className="user-details-main-panel">
                    {activeTab === 'profile' ? (
                        <UserProfileSections user={currentUser} />
                    ) : (
                        <div className="content-card team-structure-card">
                            <div className="team-structure-card-header">
                                <div className="team-structure-header-row">
                                    <div>
                                        <h3 className="card-heading">Team Structure</h3>
                                        <p className="card-description">
                                            View the reporting hierarchy and team members.
                                        </p>
                                    </div>
                                    {isTeamLead && (
                                        <button
                                            type="button"
                                            className="assign-member-btn"
                                            onClick={() => setIsAssignModalOpen(true)}
                                            title="Assign Team Member"
                                        >
                                            <Plus size={16} />
                                            <span>Assign Member</span>
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="hierarchy-tree-container">
                                <TeamStructure
                                    key={treeUserId}
                                    user={currentUser}
                                    userId={treeUserId}
                                    refreshKey={treeVersion}
                                    emptyHint='No team members assigned yet. Click "Assign Member" to add team members.'
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Right Panel: Account Overview */}
                <div className="user-details-side-panel">
                    <div className="content-card overview-card">
                        <h4 className="overview-card-title">ACCOUNT OVERVIEW</h4>
                        <div className="overview-items-list">
                            <div className="overview-item">
                                <div className="overview-icon-box status-icon">
                                    <CheckCircle2 size={16} />
                                </div>
                                <div className="overview-text">
                                    <span className="overview-item-label">STATUS</span>
                                    <span className="overview-item-value approved">
                                        {currentUser.status_label || 'Approved'}
                                    </span>
                                </div>
                            </div>

                            <div className="overview-item">
                                <div className="overview-icon-box user-icon">
                                    <User size={16} />
                                </div>
                                <div className="overview-text">
                                    <span className="overview-item-label">USER ROLE</span>
                                    <span className="overview-item-value">{currentUser.role}</span>
                                </div>
                            </div>

                            <div className="overview-item">
                                <div className="overview-icon-box org-icon">
                                    <Building2 size={16} />
                                </div>
                                <div className="overview-text">
                                    <span className="overview-item-label">ORGANIZATION</span>
                                    <span className="overview-item-value">
                                        {currentUser.organization || 'Exalogic Consulting'}
                                    </span>
                                </div>
                            </div>

                            <div className="overview-item">
                                <div className="overview-icon-box date-icon">
                                    <Calendar size={16} />
                                </div>
                                <div className="overview-text">
                                    <span className="overview-item-label">JOINED DATE</span>
                                    <span className="overview-item-value">
                                        {currentUser.joined_date || currentUser.created}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal for Assigning Team Member (matches Image 4) */}
            <AssignTeamMemberModal
                isOpen={isAssignModalOpen}
                onClose={() => setIsAssignModalOpen(false)}
                teamLead={currentUser}
                onAssignSuccess={handleAssignSuccess}
            />

            {/* Toast Banner for assign feedback */}
            {toastMessage && (
                <div className="assign-toast-banner">
                    {toastMessage}
                </div>
            )}

            {/* Bottom Footer */}
            <footer className="user-details-footer">
                <span>©2026 Copyright</span>
                <span className="footer-divider">|</span>
                <a href="#privacy">Privacy Policy</a>
                <span className="footer-divider">|</span>
                <a href="#terms">Terms & Conditions</a>
                <span className="footer-divider">|</span>
                <a href="mailto:info@exalogic.in">info@exalogic.in</a>
            </footer>
        </div>
    );
};

export default UserDetails;
